import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { 
  LanguageEntry, 
  AchievementEntry, 
  PortfolioEntry 
} from './types';
import { validateRequired, validateURL } from './utils/validation';
import Input from '../UI/Input';
import Button from '../UI/Button';
import Modal from '../UI/Modal';
import { Edit, Plus, Trash } from "lucide-react";

interface CVAdditionalSectionsProps {
  languages: LanguageEntry[];
  achievements: AchievementEntry[];
  portfolios: PortfolioEntry[];
  onAddLanguage: (language: LanguageEntry) => Promise<void>;
  onUpdateLanguage: (index: number, language: LanguageEntry) => Promise<void>;
  onDeleteLanguage: (index: number) => Promise<void>;
  onAddAchievement: (achievement: AchievementEntry) => Promise<void>;
  onUpdateAchievement: (index: number, achievement: AchievementEntry) => Promise<void>;
  onDeleteAchievement: (index: number) => Promise<void>;
  onAddPortfolio: (portfolio: PortfolioEntry) => Promise<void>;
  onUpdatePortfolio: (index: number, portfolio: PortfolioEntry) => Promise<void>;
  onDeletePortfolio: (index: number) => Promise<void>;
}

type SectionType = 'languages' | 'achievements' | 'portfolios';

/**
 * CV Additional Sections component (Languages, Achievements, Portfolio)
 * Manages languages, achievements, and portfolio entries with CRUD operations
 */
const CVAdditionalSections: React.FC<CVAdditionalSectionsProps> = ({
  languages,
  achievements,
  portfolios,
  onAddLanguage,
  onUpdateLanguage,
  onDeleteLanguage,
  onAddAchievement,
  onUpdateAchievement,
  onDeleteAchievement,
  onAddPortfolio,
  onUpdatePortfolio,
  onDeletePortfolio
}) => {
  const [showModal, setShowModal] = useState<Record<SectionType, boolean>>({
    languages: false,
    achievements: false,
    portfolios: false
  });
  const [editingId, setEditingId] = useState<Record<SectionType, number | null>>({
    languages: null,
    achievements: null,
    portfolios: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Language form
  const languageForm = useForm<LanguageEntry>({
    mode: 'onBlur',
    defaultValues: {
      language: '',
      level: 'Pemula',
      evidence: ''
    }
  });

  // Achievement form
  const achievementForm = useForm<AchievementEntry>({
    mode: 'onBlur',
    defaultValues: {
      name: '',
      year: new Date().getFullYear(),
      description: ''
    }
  });

  // Portfolio form
  const portfolioForm = useForm<PortfolioEntry>({
    mode: 'onBlur',
    defaultValues: {
      portfolio_url: '',
      work_type: 'Lainnya',
      description: ''
    }
  });

  const proficiencyLevels = [
    { value: 'Pemula', label: 'Pemula (Beginner)' },
    { value: 'Menengah', label: 'Menengah (Intermediate)' },
    { value: 'Mahir', label: 'Mahir (Advanced)' },
    { value: 'Native', label: 'Native' }
  ];

  const workTypes = [
    { value: 'Desain', label: 'Desain (Design)' },
    { value: 'Video', label: 'Video' },
    { value: 'Artikel', label: 'Artikel (Article)' },
    { value: 'Aplikasi', label: 'Aplikasi (Application)' },
    { value: 'Lainnya', label: 'Lainnya (Other)' }
  ];

  // Extract YouTube video ID for preview
  const extractYouTubeId = (url: string): string | null => {
    const regex = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  // Check if URL is YouTube
  const isYouTubeURL = (url: string): boolean => {
    return /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)/.test(url);
  };

  // Validate language entry
  const validateLanguageEntry = (data: LanguageEntry): { isValid: boolean; errors: Record<string, string[]> } => {
    const validationErrors: Record<string, string[]> = {};

    validationErrors.language = validateRequired(data.language, 'Language');

    Object.keys(validationErrors).forEach(key => {
      if (validationErrors[key].length === 0) {
        delete validationErrors[key];
      }
    });

    return {
      isValid: Object.keys(validationErrors).length === 0,
      errors: validationErrors
    };
  };

  // Validate achievement entry
  const validateAchievementEntry = (data: AchievementEntry): { isValid: boolean; errors: Record<string, string[]> } => {
    const validationErrors: Record<string, string[]> = {};

    validationErrors.name = validateRequired(data.name, 'Achievement name');
    validationErrors.description = validateRequired(data.description, 'Description');

    if (!data.year || data.year < 1950 || data.year > new Date().getFullYear()) {
      validationErrors.year = ['Please enter a valid year'];
    }

    Object.keys(validationErrors).forEach(key => {
      if (validationErrors[key].length === 0) {
        delete validationErrors[key];
      }
    });

    return {
      isValid: Object.keys(validationErrors).length === 0,
      errors: validationErrors
    };
  };

  // Validate portfolio entry
  const validatePortfolioEntry = (data: PortfolioEntry): { isValid: boolean; errors: Record<string, string[]> } => {
    const validationErrors: Record<string, string[]> = {};

    validationErrors.portfolio_url = validateRequired(data.portfolio_url, 'Portfolio URL');
    validationErrors.description = validateRequired(data.description, 'Description');

    if (data.portfolio_url) {
      const urlErrors = validateURL(data.portfolio_url, 'Portfolio URL');
      if (urlErrors.length > 0) {
        validationErrors.portfolio_url.push(...urlErrors);
      }
    }

    Object.keys(validationErrors).forEach(key => {
      if (validationErrors[key].length === 0) {
        delete validationErrors[key];
      }
    });

    return {
      isValid: Object.keys(validationErrors).length === 0,
      errors: validationErrors
    };
  };

  // Handle language form submission
  const onSubmitLanguage = async (data: LanguageEntry) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const validationResult = validateLanguageEntry(data);
      
      if (!validationResult.isValid) {
        setIsSubmitting(false);
        return;
      }

      const cleanData: LanguageEntry = {
        language: data.language.trim(),
        level: data.level,
        evidence: data.evidence?.trim() || undefined
      };

      if (editingId.languages !== null) {
        await onUpdateLanguage(editingId.languages, cleanData);
        setEditingId(prev => ({ ...prev, languages: null }));
      } else {
        await onAddLanguage(cleanData);
      }

      languageForm.reset();
      setShowModal(prev => ({ ...prev, languages: false }));
    } catch (error: any) {
      setSubmitError(error.message || 'Gagal menyimpan data bahasa. Silakan coba lagi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle achievement form submission
  const onSubmitAchievement = async (data: AchievementEntry) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const validationResult = validateAchievementEntry(data);
      
      if (!validationResult.isValid) {
        setIsSubmitting(false);
        return;
      }

      const cleanData: AchievementEntry = {
        name: data.name.trim(),
        year: Number(data.year),
        description: data.description.trim(),
        organizer: data.organizer.trim(),
      };

      if (editingId.achievements !== null) {
        await onUpdateAchievement(editingId.achievements, cleanData);
        setEditingId(prev => ({ ...prev, achievements: null }));
      } else {
        await onAddAchievement(cleanData);
      }

      achievementForm.reset();
      setShowModal(prev => ({ ...prev, achievements: false }));
    } catch (error: any) {
      setSubmitError(error.message || 'Gagal menyimpan data pencapaian. Silakan coba lagi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle portfolio form submission
  const onSubmitPortfolio = async (data: PortfolioEntry) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const validationResult = validatePortfolioEntry(data);
      
      if (!validationResult.isValid) {
        setIsSubmitting(false);
        return;
      }

      const cleanData: PortfolioEntry = {
        portfolio_url: data.portfolio_url.trim(),
        work_type: data.work_type,
        description: data.description.trim()
      };

      if (editingId.portfolios !== null) {
        await onUpdatePortfolio(editingId.portfolios, cleanData);
        setEditingId(prev => ({ ...prev, portfolios: null }));
      } else {
        await onAddPortfolio(cleanData);
      }

      portfolioForm.reset();
      setShowModal(prev => ({ ...prev, portfolios: false }));
    } catch (error: any) {
      setSubmitError(error.message || 'Gagal menyimpan item portofolio. Silakan coba lagi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle edit actions
  const handleEditLanguage = (index: number) => {
    const language = languages[index];
    if (!language.id) {
      setSubmitError('Cannot edit language entry without ID');
      return;
    }
    setEditingId(prev => ({ ...prev, languages: language.id! }));
    setShowModal(prev => ({ ...prev, languages: true }));
    
    languageForm.setValue('language', language.language);
    languageForm.setValue('level', language.level);
    languageForm.setValue('evidence', language.evidence || '');
  };

  const handleEditAchievement = (index: number) => {
    const achievement = achievements[index];
    if (!achievement.id) {
      setSubmitError('Cannot edit achievement entry without ID');
      return;
    }
    setEditingId(prev => ({ ...prev, achievements: achievement.id! }));
    setShowModal(prev => ({ ...prev, achievements: true }));
    
    achievementForm.setValue('name', achievement.name);
    achievementForm.setValue('year', achievement.year);
    achievementForm.setValue('description', achievement.description);
    achievementForm.setValue("organizer", achievement.organizer);
  };

  const handleEditPortfolio = (index: number) => {
    const portfolio = portfolios[index];
    if (!portfolio.id) {
      setSubmitError('Cannot edit portfolio entry without ID');
      return;
    }
    setEditingId(prev => ({ ...prev, portfolios: portfolio.id! }));
    setShowModal(prev => ({ ...prev, portfolios: true }));
    
    portfolioForm.setValue('portfolio_url', portfolio.portfolio_url);
    portfolioForm.setValue('work_type', portfolio.work_type);
    portfolioForm.setValue('description', portfolio.description);
  };

  // Handle delete actions
  const handleDeleteLanguage = async (index: number) => {
    const language = languages[index];
    if (!language.id) {
      setSubmitError('Cannot delete language entry without ID');
      return;
    }
    
    if (globalThis.confirm('Apakah Anda yakin ingin menghapus data bahasa ini?')) {
      try {
        await onDeleteLanguage(language.id);
      } catch (error: any) {
        setSubmitError(error.message || 'Gagal menghapus data bahasa. Silakan coba lagi.');
      }
    }
  };

  const handleDeleteAchievement = async (index: number) => {
    const achievement = achievements[index];
    if (!achievement.id) {
      setSubmitError('Cannot delete achievement entry without ID');
      return;
    }
    
    if (globalThis.confirm('Apakah Anda yakin ingin menghapus data pencapaian ini?')) {
      try {
        await onDeleteAchievement(achievement.id);
      } catch (error: any) {
        setSubmitError(error.message || 'Gagal menghapus data pencapaian. Silakan coba lagi.');
      }
    }
  };

  const handleDeletePortfolio = async (index: number) => {
    const portfolio = portfolios[index];
    if (!portfolio.id) {
      setSubmitError('Cannot delete portfolio entry without ID');
      return;
    }
    
    if (globalThis.confirm('Apakah Anda yakin ingin menghapus item portofolio ini?')) {
      try {
        await onDeletePortfolio(portfolio.id);
      } catch (error: any) {
        setSubmitError(error.message || 'Gagal menghapus item portofolio. Silakan coba lagi.');
      }
    }
  };

  // Handle cancel actions
  const handleCancel = (section: SectionType) => {
    setShowModal(prev => ({ ...prev, [section]: false }));
    setEditingId(prev => ({ ...prev, [section]: null }));
    setSubmitError(null);
    
    if (section === 'languages') languageForm.reset();
    if (section === 'achievements') achievementForm.reset();
    if (section === 'portfolios') portfolioForm.reset();
  };

  // Handle add new actions
  const handleAddNew = (section: SectionType) => {
    setShowModal(prev => ({ ...prev, [section]: true }));
    setEditingId(prev => ({ ...prev, [section]: null }));
    setSubmitError(null);
    
    if (section === 'languages') languageForm.reset();
    if (section === 'achievements') achievementForm.reset();
    if (section === 'portfolios') portfolioForm.reset();
  };

  return (
    <div className="cv-additional-sections">
      <div className="flex flex-wrap">
        <div className="w-full">
          {submitError && (
            <div className="p-4 rounded border bg-red-50 border-red-200 text-red-800 mb-3" role="alert">
              {submitError}
            </div>
          )}

          {/* Languages Section */}
          <div className="languages-section mb-5">
            <div className="flex justify-between items-center mb-4">
              <h4 className='font-semibold'>Bahasa</h4>
            </div>

            {/* Languages List */}
            {languages.length > 0 && (
              <div className="languages-entries mb-4">
                {languages.map((language, index) => (
                  <div key={index} className="bg-white rounded-lg shadow border mb-3">
                    <div className="p-4">
                      <div className="flex flex-wrap flex-col">
                        <div className="md:w-8/12">
                          <h5 className="text-lg font-semibold mb-2">
                            {language.language}
                          </h5>
                          <p className="text-gray-700 mb-1">
                            <strong>Tingkat:</strong> {language.level}
                          </p>
                          {language.evidence && (
                            <p className="text-gray-700 mb-0">
                              <strong>Bukti:</strong> {language.evidence}
                            </p>
                          )}
                        </div>
                        <div className="md:w-4/12 flex items-center justify-end gap-2">
                          <a
                            onClick={() => handleEditLanguage(index)}
                            className="cursor-pointer"
                          >
                            <Edit className="text-primary" size={"1rem"} />
                          </a>
                          <a
                            className="cursor-pointer"
                            onClick={() => handleDeleteLanguage(index)}
                          >
                            <Trash className="text-red-600" size={"1rem"} />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {languages.length > 0 && (
              <div className="flex justify-between items-center mb-4">
                <Button
                  primary
                  onClick={() => handleAddNew("languages")}
                  className="px-3 flex items-center gap-2 w-full gradient-primary"
                >
                  <Plus /> Tambah
                </Button>
              </div>
            )}
            {/* Empty State */}
            {languages.length === 0 && (
              <div className="text-center py-5">
                <p className="text-gray-500 mb-3">
                  Belum ada bahasa yang ditambahkan.
                </p>
                <Button
                  primary
                  onClick={() => handleAddNew("languages")}
                  className="px-4 inline-flex items-center gap-2 mx-auto gradient-primary"
                >
                  <Plus /> Tambah Bahasa
                </Button>
              </div>
            )}
          </div>
          <hr className="my-3" />
          {/* Achievements Section */}
          <div className="achievements-section mb-5">
            <div className="flex justify-between items-center mb-4">
              <h4 className='font-semibold'>Prestasi & Sertifikasi</h4>
            </div>
            {/* Achievements List */}
            {achievements.length > 0 && (
              <div className="achievements-entries mb-4">
                {achievements.map((achievement, index) => (
                  <div key={index} className="bg-white rounded-lg shadow border mb-3">
                    <div className="p-4">
                      <div className="flex flex-wrap flex-col">
                        <div className="md:w-8/12">
                          <h5 className="text-lg font-semibold mb-2">
                            {achievement.name}
                          </h5>
                          <p className="text-gray-700 mb-1">
                            <strong>Tahun:</strong> {achievement.year}
                          </p>
                          <p className="text-gray-700 mb-1">
                            <strong>Penyelenggara:</strong> {achievement.organizer}
                          </p>
                          <p className="text-gray-700 mb-0">
                            <strong>Deskripsi:</strong>{" "}
                            {achievement.description}
                          </p>
                        </div>
                        <div className="md:w-4/12 flex items-center justify-end gap-2">
                          <a
                            onClick={() => handleEditAchievement(index)}
                            className="cursor-pointer"
                          >
                            <Edit className="text-primary" size={"1rem"} />
                          </a>
                          <a
                            className="cursor-pointer"
                            onClick={() => handleDeleteAchievement(index)}
                          >
                            <Trash className="text-red-600" size={"1rem"} />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {achievements.length > 0 && (
              <div className="flex justify-between items-center mb-4">
                <Button
                  primary
                  onClick={() => handleAddNew("achievements")}
                  className="px-3 flex items-center gap-2 gradient-primary w-full"
                >
                  <Plus /> Tambah
                </Button>
              </div>
            )}

            {/* Empty State */}
            {achievements.length === 0 && (
              <div className="text-center py-5 flex flex-col items-center justify-center gap-2">
                <p className="text-gray-500 mb-3">
                  Belum ada Prestasi/Sertifikasi yang ditambahkan.
                </p>
                <Button
                  primary
                  onClick={() => handleAddNew("achievements")}
                  className="px-4 flex items-center gap-2 gradient-primary mx-auto"
                >
                  <Plus /> Tambah Prestasi/Sertifikasi
                </Button>
              </div>
            )}
          </div>
          <hr className="my-3" />
          {/* Portfolio Section */}
          <div className="portfolios-section mb-5">
            <div className="flex justify-between items-center mb-4">
              <h4 className='font-semibold'>Portofolio</h4>
            </div>

            {/* Portfolio List */}
            {portfolios.length > 0 && (
              <div className="portfolios-entries mb-4">
                {portfolios.map((portfolio, index) => (
                  <div key={index} className="bg-white rounded-lg shadow border mb-3">
                    <div className="p-4">
                      <div className="flex flex-wrap flex-col">
                        <div className="md:w-8/12">
                          <h5 className="text-lg font-semibold mb-2">
                            <a
                              href={portfolio.portfolio_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Portofolio {portfolio.work_type}
                            </a>
                          </h5>
                          <p className="text-gray-700 mb-1">
                            <strong>Jenis:</strong> {portfolio.work_type}
                          </p>
                          <p className="text-gray-700 mb-2">
                            <strong>Deskripsi:</strong> {portfolio.description}
                          </p>

                          {/* YouTube Video Preview */}
                          {isYouTubeURL(portfolio.portfolio_url) && (
                            <div className="youtube-preview mb-2">
                              <div
                                className="ratio ratio-16x9"
                                style={{ maxWidth: "400px" }}
                              >
                                <iframe
                                  src={`https://www.youtube.com/embed/${extractYouTubeId(
                                    portfolio.portfolio_url
                                  )}`}
                                  title="YouTube video preview"
                                  allowFullScreen
                                ></iframe>
                              </div>
                            </div>
                          )}

                          <p className="text-gray-700 mb-0">
                            <small className="text-gray-500">
                              <a
                                href={portfolio.portfolio_url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {portfolio.portfolio_url}
                              </a>
                            </small>
                          </p>
                        </div>
                        <div className="md:w-4/12 flex items-center justify-end gap-2">
                          <a
                            onClick={() => handleEditPortfolio(index)}
                            className="cursor-pointer"
                          >
                            <Edit className="text-primary" size={"1rem"} />
                          </a>
                          <a
                            className="cursor-pointer"
                            onClick={() => handleDeletePortfolio(index)}
                          >
                            <Trash className="text-red-600" size={"1rem"} />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {portfolios.length > 0 && (
              <div className="flex justify-between items-center mb-4">
                <Button
                  primary
                  onClick={() => handleAddNew("portfolios")}
                  className="px-3 flex items-center gap-2 w-full gradient-primary"
                >
                  <Plus /> Tambah
                </Button>
              </div>
            )}
            {/* Empty State */}
            {portfolios.length === 0 && (
              <div className="text-center py-5 flex flex-col items-center justify-center gap-2">
                <p className="text-gray-500 mb-3">
                  Belum ada portofolio yang ditambahkan.
                </p>
                <Button
                  primary
                  onClick={() => handleAddNew("portfolios")}
                  className="px-4 flex items-center gap-2 gradient-primary mx-auto"
                >
                  <Plus /> Tambah Portofolio
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Language Modal */}
      <Modal
        show={showModal.languages}
        onHide={() => handleCancel("languages")}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingId.languages !== null
              ? "Ubah Data Bahasa"
              : "Tambah Data Bahasa Baru"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {submitError && (
            <div className="p-4 rounded border bg-red-50 border-red-200 text-red-800 mb-3" role="alert">
              {submitError}
            </div>
          )}

          <form
            onSubmit={languageForm.handleSubmit(onSubmitLanguage)}
            noValidate
          >
            <div className="flex flex-wrap">
              <div className="w-full md:w-6/12 mb-3">
                <label htmlFor="language" className="form-label">
                  Bahasa <span className="text-red-600">*</span>
                </label>
                <Input
                  placeholder="Masukkan nama bahasa"
                  name="language"
                  register={languageForm.register as any}
                  errors={languageForm.formState.errors as any}
                  rules={{
                    required: "Bahasa wajib diisi",
                    minLength: {
                      value: 2,
                      message: "Nama bahasa minimal 2 karakter",
                    },
                  }}
                  block
                  disabled={isSubmitting}
                />
              </div>

              <div className="w-full md:w-6/12 mb-3">
                <label htmlFor="level" className="form-label">
                  Tingkat Kemahiran <span className="text-red-600">*</span>
                </label>
                <select
                  id="level"
                  className="w-full bg-transparent placeholder:text-slate-400 text-slate-700 border border-slate-200 rounded-xl pl-2 pr-8 py-2 transition duration-300 ease focus:outline-none focus:border-slate-400 hover:border-slate-400 shadow-sm focus:shadow-md cursor-pointer"
                  {...languageForm.register("level", {
                    required: "Tingkat kemahiran wajib diisi",
                  })}
                  disabled={isSubmitting}
                >
                  <option value="">Pilih tingkat kemahiran</option>
                  {proficiencyLevels.map((level, idx) => (
                    <option key={idx} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
                {languageForm.formState.errors.level && (
                  <p className="text-red-600 italic mb-2">
                    {languageForm.formState.errors.level.message}
                  </p>
                )}
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="evidence" className="form-label">
                Bukti/Sertifikasi <span className="text-gray-500">(Opsional)</span>
              </label>
              <Input
                placeholder="contoh: TOEFL 550, IELTS 7.0, Penutur asli"
                name="evidence"
                register={languageForm.register as any}
                errors={languageForm.formState.errors as any}
                block
                disabled={isSubmitting}
              />
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            outline
            onClick={() => handleCancel("languages")}
            disabled={isSubmitting}
            className="px-4"
          >
            Batal
          </Button>
          <Button
            type="submit"
            primary
            disabled={isSubmitting}
            className="px-4 gradient-primary"
            onClick={languageForm.handleSubmit(onSubmitLanguage)}
          >
            {isSubmitting
              ? "Menyimpan..."
              : editingId.languages !== null
              ? "Perbarui Bahasa"
              : "Tambah Bahasa"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Achievement Modal */}
      <Modal
        show={showModal.achievements}
        onHide={() => handleCancel("achievements")}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingId.achievements !== null
              ? "Ubah Data Prestasi & Sertifikasi"
              : "Tambah Data Prestasi & Sertifikasi Baru"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {submitError && (
            <div className="p-4 rounded border bg-red-50 border-red-200 text-red-800 mb-3" role="alert">
              {submitError}
            </div>
          )}

          <form
            onSubmit={achievementForm.handleSubmit(onSubmitAchievement)}
            noValidate
          >
            <div className="flex flex-wrap">
              <div className="w-full md:w-8/12 mb-3">
                <label htmlFor="name" className="form-label">
                  Nama Pencapaian <span className="text-red-600">*</span>
                </label>
                <Input
                  placeholder="Masukkan nama prestasi/sertifikasi"
                  name="name"
                  register={achievementForm.register as any}
                  errors={achievementForm.formState.errors as any}
                  rules={{
                    required: "Nama pencapaian wajib diisi",
                    minLength: {
                      value: 2,
                      message: "Nama pencapaian minimal 2 karakter",
                    },
                  }}
                  block
                  disabled={isSubmitting}
                />
              </div>

              <div className="w-full md:w-4/12 mb-3">
                <label htmlFor="year" className="form-label">
                  Tahun <span className="text-red-600">*</span>
                </label>
                <Input
                  placeholder="2024"
                  type="number"
                  name="year"
                  register={achievementForm.register as any}
                  errors={achievementForm.formState.errors as any}
                  rules={{
                    required: "Tahun wajib diisi",
                    min: {
                      value: 1950,
                      message: "Tahun harus setelah 1950",
                    },
                    max: {
                      value: new Date().getFullYear(),
                      message: "Tahun tidak boleh di masa depan",
                    },
                  }}
                  block
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div className="w-full md:w-8/12 mb-3">
              <label htmlFor="name" className="form-label">
                Penyelenggara <span className="text-red-600">*</span>
              </label>
              <Input
                placeholder="Masukkan nama penyelenggara"
                name="organizer"
                register={achievementForm.register as any}
                errors={achievementForm.formState.errors as any}
                rules={{
                  required: "Nama penyelenggara wajib diisi",
                  minLength: {
                    value: 2,
                    message: "Nama penyelenggara minimal 2 karakter",
                  },
                }}
                block
                disabled={isSubmitting}
              />
            </div>
            <div className="w-full mb-4">
              <label htmlFor="description" className="form-label">
                Deskripsi <span className="text-red-600">*</span>
              </label>
              <textarea
                id="description"
                className="form-control"
                placeholder="Jelaskan prestasi/sertifikasi Anda"
                rows={3}
                {...achievementForm.register("description", {
                  required: "Deskripsi wajib diisi",
                  minLength: {
                    value: 10,
                    message: "Deskripsi minimal 10 karakter",
                  },
                })}
                disabled={isSubmitting}
              />
              {achievementForm.formState.errors.description && (
                <p className="text-red-600 italic mb-2">
                  {achievementForm.formState.errors.description.message}
                </p>
              )}
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            outline
            onClick={() => handleCancel("achievements")}
            disabled={isSubmitting}
            className="px-4"
          >
            Batal
          </Button>
          <Button
            type="submit"
            primary
            disabled={isSubmitting}
            className="px-4 gradient-primary"
            onClick={achievementForm.handleSubmit(onSubmitAchievement)}
          >
            {isSubmitting
              ? "Menyimpan..."
              : editingId.achievements !== null
              ? "Perbarui Prestasi & Sertifikasi"
              : "Tambah Prestasi & Sertifikasi"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Portfolio Modal */}
      <Modal
        show={showModal.portfolios}
        onHide={() => handleCancel("portfolios")}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingId.portfolios !== null
              ? "Ubah Item Portofolio"
              : "Tambah Item Portofolio Baru"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {submitError && (
            <div className="p-4 rounded border bg-red-50 border-red-200 text-red-800 mb-3" role="alert">
              {submitError}
            </div>
          )}

          <form
            onSubmit={portfolioForm.handleSubmit(onSubmitPortfolio)}
            noValidate
          >
            <div className="flex flex-wrap">
              <div className="w-full md:w-8/12 mb-3">
                <label htmlFor="portfolio_url" className="form-label">
                  URL Portofolio <span className="text-red-600">*</span>
                </label>
                <Input
                  placeholder="https://contoh.com atau https://youtube.com/watch?v=..."
                  name="portfolio_url"
                  register={portfolioForm.register as any}
                  errors={portfolioForm.formState.errors as any}
                  rules={{
                    required: "URL portofolio wajib diisi",
                    pattern: {
                      value: /^https?:\/\/.+/,
                      message:
                        "Masukkan URL yang valid dimulai dengan http:// atau https://",
                    },
                  }}
                  block
                  disabled={isSubmitting}
                />
              </div>

              <div className="w-full md:w-4/12 mb-3">
                <label htmlFor="work_type" className="form-label">
                  Jenis Karya <span className="text-red-600">*</span>
                </label>
                <select
                  id="work_type"
                  className="w-full bg-transparent placeholder:text-slate-400 text-slate-700 border border-slate-200 rounded-xl pl-2 pr-8 py-2 transition duration-300 ease focus:outline-none focus:border-slate-400 hover:border-slate-400 shadow-sm focus:shadow-md cursor-pointer"
                  {...portfolioForm.register("work_type", {
                    required: "Jenis karya wajib diisi",
                  })}
                  disabled={isSubmitting}
                >
                  <option value="">Pilih jenis karya</option>
                  {workTypes.map((type, idx) => (
                    <option key={idx} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                {portfolioForm.formState.errors.work_type && (
                  <p className="text-red-600 italic mb-2">
                    {portfolioForm.formState.errors.work_type.message}
                  </p>
                )}
              </div>
            </div>

            <div className="w-full mb-4">
              <label htmlFor="portfolio_description" className="form-label">
                Deskripsi <span className="text-red-600">*</span>
              </label>
              <textarea
                id="portfolio_description"
                className="form-control"
                placeholder="Jelaskan karya portofolio Anda"
                rows={3}
                {...portfolioForm.register("description", {
                  required: "Deskripsi wajib diisi",
                  minLength: {
                    value: 10,
                    message: "Deskripsi minimal 10 karakter",
                  },
                })}
                disabled={isSubmitting}
              />
              {portfolioForm.formState.errors.description && (
                <p className="text-red-600 italic mb-2">
                  {portfolioForm.formState.errors.description.message}
                </p>
              )}
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            outline
            onClick={() => handleCancel("portfolios")}
            disabled={isSubmitting}
            className="px-4"
          >
            Batal
          </Button>
          <Button
            type="submit"
            primary
            disabled={isSubmitting}
            className="px-4 gradient-primary"
            onClick={portfolioForm.handleSubmit(onSubmitPortfolio)}
          >
            {isSubmitting
              ? "Menyimpan..."
              : editingId.portfolios !== null
              ? "Perbarui Portofolio"
              : "Tambah Portofolio"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CVAdditionalSections;